import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/landing/Navbar";
import { ArrowLeft, BarChart3, ArrowUp, CircleDot, CheckCircle2, Zap, AlertTriangle, FileText } from "lucide-react";

const STEPS = [
  { num: "01", icon: BarChart3, title: "Pick a market", desc: "Open the Qovarix app and choose a supported asset — BTC, ETH, or others as they become available.", color: "#00F0FF" },
  { num: "02", icon: ArrowUp, title: "Choose your direction", desc: "Will the price be higher or lower at the end of the round? Select UP or DOWN.", color: "#00F0FF" },
  { num: "03", icon: CircleDot, title: "Enter your position", desc: "Set how much you want to stake. Your funds are held in a non-custodial smart contract vault on Base — not by Qovarix.", color: "#B5FF00" },
  { num: "04", icon: CheckCircle2, title: "Wait for settlement", desc: "Each round lasts 15–30 seconds. When the round ends, the result is determined by the price feed and winners share the pool.", color: "#B5FF00" },
];

const AI_STEPS = [
  "Before placing a trade, look at the AI signal panel.",
  "The engine shows a bullish/bearish probability, confidence level, and momentum direction.",
  "Use it as one input — not the only input. AI signals are advisory, not guarantees.",
  "Combine with your own market read for better decisions.",
];

export default function LearnPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen" style={{ background: "#030406" }}>
      <Navbar />
      <div className="pt-24 pb-12 px-6 border-b border-white/5">
        <div className="max-w-4xl mx-auto">
          <Link to="/" className="font-mono text-xs text-slate-500 hover:text-[#00F0FF] transition-colors inline-flex items-center gap-1 mb-6">
            <ArrowLeft size={12} /> Back to home
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#00F0FF] mb-3 block">DOCS / LEARN</span>
            <h1 className="font-grotesk text-4xl sm:text-5xl font-bold text-white tracking-tight">How to trade on Qovarix</h1>
            <p className="font-grotesk text-lg text-slate-400 mt-4 max-w-2xl">
              A simple guide to placing your first prediction market trade. No complex setup — just pick, predict, and settle.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-16">
        {/* Steps */}
        <h2 className="font-grotesk text-2xl font-bold text-white mb-8">Trading flow</h2>
        <div className="space-y-4 mb-16">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div key={s.num} className="glass-panel-active p-6 flex items-start gap-5" initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: i * 0.1 }}>
                <div className="flex flex-col items-center shrink-0">
                  <span className="font-mono text-2xl font-bold" style={{ color: s.color }}>{s.num}</span>
                  <Icon size={20} className="mt-2" style={{ color: s.color }} strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-grotesk text-lg font-semibold text-white mb-1">{s.title}</h3>
                  <p className="font-grotesk text-sm text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* AI Signals */}
        <h2 className="font-grotesk text-2xl font-bold text-white mb-6 flex items-center gap-2">
          <Zap size={20} className="text-[#00F0FF]" /> Using AI signals
        </h2>
        <div className="glass-panel p-6 mb-8">
          <ol className="space-y-3">
            {AI_STEPS.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="font-mono text-xs text-[#00F0FF] mt-0.5 shrink-0">{i + 1}.</span>
                <span className="font-grotesk text-sm text-slate-300">{step}</span>
              </li>
            ))}
          </ol>
        </div>

        {/* Risk note */}
        <div className="glass-panel p-5 border-l-2 border-[#B5FF00]/40 mb-12">
          <div className="flex items-start gap-3">
            <AlertTriangle size={16} className="text-[#B5FF00] mt-0.5 shrink-0" />
            <div>
              <h3 className="font-grotesk text-sm font-semibold text-white mb-1">Risk reminder</h3>
              <p className="font-grotesk text-xs text-slate-500">Trading prediction markets involves substantial risk, including possible loss of your entire position. AI signals are advisory only and do not guarantee outcomes. Never trade more than you can afford to lose.</p>
            </div>
          </div>
        </div>

        <Link to="/docs/whitepaper" className="font-mono text-xs uppercase tracking-[0.15em] text-[#00F0FF] border border-[#00F0FF]/20 hover:border-[#00F0FF]/50 px-5 py-3 transition-colors inline-flex items-center gap-2">
          <FileText size={14} /> Read the full Whitepaper
        </Link>
      </div>
    </div>
  );
}
